import { useEffect, useMemo, useState } from "react";

import type { Topic, TopicElement, TopicSet, TopicSummary, TopicsApi } from "@skilitics/nodes-unified";

import { getRequiredAppSetting } from "../shared/index.ts";
import { createApiCreator } from "./infrastructure/index.ts";

const endpoint = getRequiredAppSetting(import.meta.env.REACT_APP_TOPICS_ENDPOINT, "TOPICS_ENDPOINT");
const apiCreator = createApiCreator(endpoint);
const queryTopicSets = apiCreator.getCreate<TopicSet[]>(`/topic-sets`);
const addTopicSet = (name: string): Promise<TopicSet> =>
  apiCreator.postCreate<Omit<TopicSet, "id">, TopicSet>(`/topic-sets`)({ name });

export const useTopicsApi = ({ disabled }: { disabled?: boolean } = {}): TopicsApi => {
  const [topicSetId, setTopicSetId] = useState<number>();

  useEffect(() => {
    if (!topicSetId && !disabled) {
      void (async () => {
        const topicSets = await queryTopicSets();
        const FIXED_TOPIC_SET_NAME = "Temp Hard Coded Actual One Used Until We Support Topic Sets (tm)";
        let fixedTopicSet = topicSets.find((t) => t.name === FIXED_TOPIC_SET_NAME);
        if (!fixedTopicSet) {
          fixedTopicSet = await addTopicSet(FIXED_TOPIC_SET_NAME);
        }
        setTopicSetId(fixedTopicSet.id);
      })();
    }
  }, [disabled, topicSetId]);

  return useMemo(
    (): TopicsApi => ({
      setTopicSetId,
      topicSets: {
        query: queryTopicSets,
        add: addTopicSet,
      },
      topics: {
        getById: async (id) => apiCreator.getCreate<Topic[]>(`/topic-sets/${topicSetId}/topics/${id}`)(),
        query: async (elements = [], { signal } = {}) => {
          const query = `elements=${elements.map((e) => `${encodeURIComponent(e)}`).join(",")}`;
          return apiCreator.getCreate<TopicSummary[]>(`/topic-sets/${topicSetId}/topics?${query}`)({
            signal,
          });
        },
        create: async (elements: string[]): Promise<Topic> => {
          if (!elements.length) {
            throw new Error(`Expected elements to not be empty.`);
          }
          return apiCreator.postCreate<Omit<Topic, "id">, Topic>(`/topic-sets/${topicSetId}/topics`)({
            elements,
          });
        },
      },
      elements: {
        query: async (prefix: string, { signal } = {}) => {
          return apiCreator.getCreate<TopicElement[]>(`/topic-elements?topicSetIds=${topicSetId}&prefix=${prefix}`)({
            signal,
          });
        },
      },
    }),
    [topicSetId],
  );
};
